import React, { memo, ReactElement, useMemo } from "react";

// interfaces

import { CART_ERROR_CTA, CART_STATUS_CODES, ItemLimitExceededItem } from "@swiggy-private/cart-types";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { format } from "../../utils/String";
import { AriaHidden } from "../accessibility";
import { HalfCardHOC } from "../halfCardHOC";
import {
    ErrorWidgetEventPayload,
    buildErrorHalfCardEventPayload,
    getErrorWidgetAttrs,
    getErrorWidgetMessage,
} from "../../helpers/Error";
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";

// constants
import { ERROR_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";

// components
import ItemsList from "./ItemsList";
import { ActionButton, ActionsSection, Container, ContainerMeta, MetaTextWrapper, SubTitleText } from "./styled";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { ErrorWidgetAttr } from "@core-ui/interfaces/cart";
import { CLICKED_CTA } from "./constants";

export interface ErrorWidgetProps {
    statusCode: CART_STATUS_CODES;
    titleMessage?: string;
    statusMessage?: string;
    ctaText?: string;
    secondaryCtaText?: string;
    ctaIcon?: string;
    ctaAction?: string;
    secondaryCtaAction?: string;
    unServiceableItemsCount?: number;
    action: (cta: string) => void;
    isIrctcCart?: boolean;
    isPopCart?: boolean;
    getErrorWidgetAttrs?: (props: ErrorWidgetProps) => ErrorWidgetAttr;
    errorData?: {
        maxAllowedCartWeightInGrams?: number;
        maxAllowedCartVolumeInCc?: number;
        restaurantName?: string;
        itemLimitExceededList?: ItemLimitExceededItem[];
    };
    onImpression?: (payload: ErrorWidgetEventPayload) => void;
    onClickAnalytics?: (payload: ErrorWidgetEventPayload) => void;
    /** Swaps the CTA labels for a loader and makes them inert while a cart update is in flight. */
    isCtaLoading?: boolean;
    titleMaxLines?: number;
    isQuickCheckout?: boolean;
}

function ErrorWidget(props: ErrorWidgetProps): ReactElement {
    const getAttrs = props?.getErrorWidgetAttrs;
    const attrs = getAttrs ? getAttrs(props) : getErrorWidgetAttrs(props);
    const { title, message, cta, secondaryCtaText, headerIcon, genericIcon } = attrs ?? {};
    const unaviableItemsCount = props.unServiceableItemsCount?.toString() || "";
    const finalMessage = useMemo(
        () => getErrorWidgetMessage(message, props.statusCode, props.errorData),
        [message, props.statusCode, props.errorData],
    );
    const itemsList = props.errorData?.itemLimitExceededList;
    let finalTitle = title;

    // repalce items count if error is due to unavailable items
    if (props.ctaAction && props.ctaAction.toLowerCase() === CART_ERROR_CTA.REMOVE_UNAVAILABLE_ITEMS) {
        finalTitle = format(title, unaviableItemsCount);
    }

    const ctaActionsArrowEligible = [
        CART_ERROR_CTA.GOTO_SIMILAR_RESTAURANTS,
        CART_ERROR_CTA.VIEW_SIMILAR_RESTAURANTS,
        CART_ERROR_CTA.GO_TO_HOME,
    ];
    const showArrowIcon =
        props.ctaAction && ctaActionsArrowEligible.includes(props.ctaAction?.toLowerCase() as CART_ERROR_CTA);

    const onClick = React.useCallback(() => {
        props.onClickAnalytics?.(buildErrorHalfCardEventPayload(props, CLICKED_CTA.PRIMARY));
        props.action(props.ctaAction || "retry");
    }, [props]);

    const handleSecondaryCtaClick = React.useCallback(() => {
        props.onClickAnalytics?.(buildErrorHalfCardEventPayload(props, CLICKED_CTA.SECONDARY));
        props.action(props.secondaryCtaAction || "retry");
    }, [props]);

    const actionButtonType = React.useMemo(() => {
        if (props.isIrctcCart) {
            if (
                props.ctaAction === CART_ERROR_CTA.ENTER_PNR ||
                props.ctaAction === CART_ERROR_CTA.SELECT_ANOTHER_STATION
            ) {
                return "primary";
            }

            return props?.isIrctcCart ? "tertiary" : "primary";
        }
        return "primary";
    }, [props.isIrctcCart, props.ctaAction]);

    useEffectOnce(() => {
        props.onImpression?.(buildErrorHalfCardEventPayload(props));
    });

    return (
        <Container data-testid={ERROR_WIDGET_TEST_IDS.CONTAINER}>
            <ContainerMeta>
                <AriaHidden as="div">
                    {genericIcon ? (
                        <GenericIcon Icon={genericIcon} fillColor="negative" strokeColor="negative" />
                    ) : (
                        <SpriteIconWeb
                            iconName={headerIcon || "errorFilled20"}
                            fillColor="negative"
                            strokeColor="negative"
                        />
                    )}
                </AriaHidden>
                <MetaTextWrapper>
                    <Text
                        variant="V2.subheading1.neutral.black"
                        color="text_High_Emphasis"
                        data-testid={ERROR_WIDGET_TEST_IDS.TITLE}
                        maxLines={props.titleMaxLines}
                    >
                        {finalTitle}
                    </Text>
                    {!isEmpty(finalMessage) && (
                        <SubTitleText
                            data-testid={ERROR_WIDGET_TEST_IDS.MESSAGE}
                            variant="V2.body3.neutral.regular"
                            color="text_Med_Emphasis"
                            isQuickCheckout={props.isQuickCheckout}
                        >
                            {finalMessage}
                        </SubTitleText>
                    )}
                </MetaTextWrapper>
            </ContainerMeta>

            {!isEmpty(itemsList) && <ItemsList items={itemsList} />}

            {!isEmpty(cta) && (
                <ActionsSection>
                    {!isEmpty(secondaryCtaText) && (
                        <ActionButton
                            onClick={handleSecondaryCtaClick}
                            buttonType={"secondary"}
                            buttonSize="large"
                            variant="fullwidth"
                            data-testid={ERROR_WIDGET_TEST_IDS.CTA_BUTTON_SECONDARY}
                            loading={props.isCtaLoading}
                        >
                            {secondaryCtaText}
                        </ActionButton>
                    )}
                    <ActionButton
                        onClick={onClick}
                        buttonType={actionButtonType}
                        buttonSize="large"
                        variant="fullwidth"
                        data-testid={ERROR_WIDGET_TEST_IDS.CTA_BUTTON}
                        rightIconName={showArrowIcon ? "arrowFront20" : undefined}
                        loading={props.isCtaLoading}
                    >
                        {cta}
                    </ActionButton>
                </ActionsSection>
            )}
        </Container>
    );
}

const MemoizedErrorWidget = memo(ErrorWidget);

// Conditionally apply HalfCardHOC based on showOverlay
const ErrorWidgetWithConditionalHOC = (props: ErrorWidgetProps) => {
    const getErrorAttrs = props.getErrorWidgetAttrs ?? getErrorWidgetAttrs;
    const { showOverlay } = getErrorAttrs(props);

    if (showOverlay) {
        const HalfCardWrappedErrorWidget = HalfCardHOC(MemoizedErrorWidget);
        return <HalfCardWrappedErrorWidget {...props} />;
    }

    return <MemoizedErrorWidget {...props} />;
};

export default ErrorWidgetWithConditionalHOC;
